import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Package, Heart, MapPin, LogOut, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Navigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ProductCardV2 from '../components/ProductCardV2';
import { useSEO } from '../utils/seo';
import { readResponse } from '../utils/checkout';
import { ConfigurationSummary, formatOrderTotal } from '../utils/orderPresentation';
import { characterName } from '../lib/characters';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import { translateMessage } from '../i18n/apiErrors';
import './AnnexesAtelier.css';
const API = (process.env.REACT_APP_BACKEND_URL || '');

const COPY = {
  fr: {
    seoTitle: 'Mon compte — Le Placard à Doudous', seoDescription: 'Gérez vos commandes, votre wishlist et vos adresses.',
    statuses: { pending: 'En attente', paid: 'Payée', preparing: 'En préparation', shipped: 'Expédiée', delivered: 'Livrée', cancelled: 'Annulée', expired: 'Paiement expiré', payment_failed: 'Paiement échoué' },
    defaultLabel: 'Maison', loadUnavailable: 'Chargement indisponible', invalidResponse: 'Réponse invalide', loadingAccount: 'Chargement de votre compte…',
    accountUnavailable: 'Votre compte est momentanément indisponible.', retry: 'Réessayer', addAddressError: 'L’adresse n’a pas pu être ajoutée', addressAdded: 'Adresse ajoutée !',
    error: 'Erreur', deleteUnavailable: 'Suppression indisponible', addressDeleted: 'Adresse supprimée', profileUpdated: 'Profil mis à jour', updateError: 'Erreur lors de la mise à jour',
    connectionError: 'Erreur de connexion', mySpace: 'Mon espace', hello: name => `Bonjour, ${name} !`, logout: 'Déconnexion',
    orders: 'Commandes', wishlist: 'Wishlist', addresses: 'Adresses', profile: 'Profil', loadingOrders: 'Chargement des commandes',
    ordersError: 'Vos commandes n’ont pas pu être chargées.', noOrders: 'Pas encore de commandes', discoverShop: 'Découvrir la boutique', order: 'Commande',
    loadingWishlist: 'Chargement de la wishlist', wishlistError: 'Votre wishlist n’a pas pu être chargée.', emptyWishlist: 'Votre wishlist est vide', seeShop: 'Voir la boutique',
    myAddresses: 'Mes adresses', noAddress: 'Aucune adresse enregistrée.', deleteAddress: label => `Supprimer l’adresse ${label}`, addAddress: 'Ajouter une adresse',
    firstName: 'Prénom', lastName: 'Nom', address: 'Adresse', postalCode: 'Code postal', city: 'Ville', adding: 'Ajout...', add: 'Ajouter',
    myInfo: 'Mes informations', fullName: 'Nom complet', newPassword: 'Nouveau mot de passe (optionnel)', keepPassword: 'Laisser vide pour ne pas changer',
    updating: 'Mise à jour...', updateProfile: 'Mettre à jour mon profil', admin: 'Administrateur',
  },
  en: {
    seoTitle: 'My account — Le Placard à Doudous', seoDescription: 'Manage your orders, wishlist and addresses.',
    statuses: { pending: 'Pending', paid: 'Paid', preparing: 'Being prepared', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled', expired: 'Payment expired', payment_failed: 'Payment failed' },
    defaultLabel: 'Home', loadUnavailable: 'Loading unavailable', invalidResponse: 'Invalid response', loadingAccount: 'Loading your account…',
    accountUnavailable: 'Your account is temporarily unavailable.', retry: 'Try again', addAddressError: 'The address couldn’t be added', addressAdded: 'Address added!',
    error: 'Something went wrong', deleteUnavailable: 'Deletion unavailable', addressDeleted: 'Address deleted', profileUpdated: 'Profile updated', updateError: 'Something went wrong while updating',
    connectionError: 'Connection error', mySpace: 'My space', hello: name => `Hello, ${name}!`, logout: 'Log out',
    orders: 'Orders', wishlist: 'Wishlist', addresses: 'Addresses', profile: 'Profile', loadingOrders: 'Loading orders',
    ordersError: 'Your orders couldn’t be loaded.', noOrders: 'No orders yet', discoverShop: 'Discover the shop', order: 'Order',
    loadingWishlist: 'Loading wishlist', wishlistError: 'Your wishlist couldn’t be loaded.', emptyWishlist: 'Your wishlist is empty', seeShop: 'Visit the shop',
    myAddresses: 'My addresses', noAddress: 'No saved address.', deleteAddress: label => `Delete the ${label} address`, addAddress: 'Add an address',
    firstName: 'First name', lastName: 'Last name', address: 'Address', postalCode: 'Postcode', city: 'City', adding: 'Adding...', add: 'Add',
    myInfo: 'My details', fullName: 'Full name', newPassword: 'New password (optional)', keepPassword: 'Leave blank to keep your current one',
    updating: 'Updating...', updateProfile: 'Update my profile', admin: 'Administrator',
  },
};
const STATUS_COLORS = { pending: 'bg-yellow-100 text-yellow-800', paid: 'bg-green-100 text-green-800', preparing: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-gray-100 text-gray-700', cancelled: 'bg-red-100 text-red-800' };

export default function Account() {
  const c = useCopy(COPY);
  const { locale, lp, date, money } = useLocale();
  const orderTotal = value => (locale === 'en' ? money(Number(value)) : formatOrderTotal(value));
  useSEO(c.seoTitle, c.seoDescription);
  const { user, token, logout, refreshUser, loading: authLoading, error: authError } = useAuth();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const emptyAddress = { label: c.defaultLabel, first_name: '', last_name: '', address1: '', city: '', postal_code: '', country: 'France', phone: '' };
  const [addrForm, setAddrForm] = useState({ label: c.defaultLabel, first_name: '', last_name: '', address1: '', city: '', postal_code: '', country: 'France', phone: '' });
  const [addrLoading, setAddrLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(false);
  const [wishlistError, setWishlistError] = useState(false);
  const [retry, setRetry] = useState(0);

  const [profileForm, setProfileForm] = useState({ name: user?.name || '', password: '' });
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    if (!user || !token) return;
    const controller = new AbortController();
    const headers = { Authorization: `Bearer ${token}` };
    setOrdersLoading(true); setWishlistLoading(true);
    setOrdersError(false); setWishlistError(false);
    const loadList = (path, setter, setError, setLoading) => {
      fetch(`${API}${path}`, { headers, signal: controller.signal })
        .then(r => readResponse(r, c.loadUnavailable))
        .then(data => {
          if (!Array.isArray(data)) throw new Error(c.invalidResponse);
          if (!controller.signal.aborted) setter(data);
        })
        .catch(() => { if (!controller.signal.aborted) setError(true); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    };
    loadList('/api/orders/my-orders', setOrders, setOrdersError, setOrdersLoading);
    loadList('/api/user/wishlist', setWishlist, setWishlistError, setWishlistLoading);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, retry]);
  useEffect(() => { setProfileForm(prev => ({ ...prev, name: user?.name || '' })); }, [user?.name]);

  // Attendre la fin du chargement du profil avant de décider : un refresh avec un
  // token valide ne doit pas être redirigé vers /connexion (race condition).
  if (authLoading) return <div className="annexes-atelier min-h-screen bg-background" aria-busy="true"><div className="annexes-container"><p role="status" className="annexes-loading">{c.loadingAccount}</p></div></div>;
  if (!user && token && authError) return <div className="annexes-atelier min-h-screen px-4 pt-32 text-center"><div className="annexes-container annexes-empty" role="alert"><p className="mb-4">{c.accountUnavailable}</p><Button className="annexes-action" onClick={refreshUser}>{c.retry}</Button></div></div>;
  if (!user) return <Navigate to={lp('/connexion')} />;

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setAddrLoading(true);
    try {
      const res = await fetch(`${API}/api/user/addresses`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(addrForm) });
      await readResponse(res, c.addAddressError);
      await refreshUser(); toast.success(c.addressAdded); setAddrForm(emptyAddress);
    } catch (err) { toast.error(err.message || c.error); }
    setAddrLoading(false);
  };

  const handleDeleteAddress = async (id) => {
    try {
      const response = await fetch(`${API}/api/user/addresses/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error(c.deleteUnavailable);
      await refreshUser();
      toast.success(c.addressDeleted);
    } catch { toast.error(c.error); }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      const body = {};
      if (profileForm.name !== user.name) body.name = profileForm.name;
      if (profileForm.password) body.password = profileForm.password;

      const res = await fetch(`${API}/api/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        await refreshUser();
        toast.success(c.profileUpdated);
        setProfileForm(f => ({ ...f, password: '' })); // clear password
      } else {
        const data = await res.json();
        toast.error(translateMessage(typeof data.detail === 'string' ? data.detail : '', locale) || c.updateError);
      }
    } catch { toast.error(c.connectionError); }
    setProfileLoading(false);
  };

  return (
    <div className="annexes-atelier annexes-account min-h-screen pt-28 sm:pt-32">
      <div className="annexes-container max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        {/* Header */}
        <div className="annexes-account-header flex flex-wrap items-center justify-between gap-4 mb-10">
          <div className="min-w-0">
            <p className="annexes-eyebrow font-sans text-xs uppercase tracking-widest text-[hsl(var(--brand-fuchsia-ink))] mb-1">{c.mySpace}</p>
            <h1 className="break-words font-serif text-3xl text-foreground">{c.hello(user.name)}</h1>
          </div>
          <Button variant="outline" onClick={logout} className="annexes-secondary rounded-xl font-sans text-sm gap-2">
            <LogOut className="h-4 w-4" /> {c.logout}
          </Button>
        </div>

        <Tabs defaultValue="orders">
          <TabsList className="annexes-tabs grid h-auto w-full grid-cols-2 sm:grid-cols-4 rounded-xl mb-8 bg-[hsl(var(--muted))]">
            <TabsTrigger value="orders" className="rounded-lg font-sans text-sm gap-2" data-testid="account-orders-tab">
              <Package className="h-4 w-4" /> {c.orders}
            </TabsTrigger>
            <TabsTrigger value="wishlist" className="rounded-lg font-sans text-sm gap-2" data-testid="account-wishlist-tab">
              <Heart className="h-4 w-4" /> {c.wishlist}
            </TabsTrigger>
            <TabsTrigger value="addresses" className="rounded-lg font-sans text-sm gap-2">
              <MapPin className="h-4 w-4" /> {c.addresses}
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-lg font-sans text-sm gap-2">
              <User className="h-4 w-4" /> {c.profile}
            </TabsTrigger>
          </TabsList>

          {/* Orders */}
          <TabsContent value="orders">
            {ordersLoading ? (
              <div className="space-y-4" aria-busy="true" aria-label={c.loadingOrders}>{[...Array(3)].map((_, i) => <Skeleton key={i} className="annexes-skeleton h-24 rounded-2xl" />)}</div>
            ) : ordersError ? (
              <div role="alert" className="annexes-empty space-y-4 py-8 text-center"><p>{c.ordersError}</p><Button className="annexes-action" onClick={() => setRetry(value => value + 1)}>{c.retry}</Button></div>
            ) : orders.length === 0 ? (
              <div className="annexes-empty text-center py-16">
                <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))]" />
                <p className="font-serif text-xl text-foreground mb-2">{c.noOrders}</p>
                <Button asChild className="annexes-action rounded-xl font-sans mt-4 bg-primary text-primary-foreground">
<Link to={lp('/boutique')}>{c.discoverShop}</Link>
</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <motion.div key={order.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="annexes-card annexes-order rounded-2xl bg-card shadow-soft p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div className="min-w-0 break-words">
                        <p className="font-sans text-xs text-[hsl(var(--muted-foreground))]">{c.order}</p>
                        <p className="font-serif text-base text-[hsl(var(--primary))]">{order.order_number}</p>
                        <p className="font-sans text-xs text-[hsl(var(--muted-foreground))] mt-1">{date(order.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={`rounded-full px-3 py-1 font-sans text-xs border-0 ${STATUS_COLORS[order.status] || 'bg-gray-100'}`}>{c.statuses[order.status] || order.status}</Badge>
                        <p className="font-sans text-sm font-semibold text-[hsl(var(--primary))] mt-1">{orderTotal(order.total)}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {order.items?.map((item, i) => (
                        <div key={i} className="annexes-order-item min-w-0 w-full bg-[hsl(var(--muted))]/40 rounded-xl px-3 py-2">
                          <p className="break-words font-sans text-xs">{characterName(item.product_slug || item.slug, item.name, locale)} × {item.quantity}</p>
                          <ConfigurationSummary configuration={item.configuration} />
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Wishlist */}
          <TabsContent value="wishlist">
            {wishlistLoading ? (
              <div className="annexes-wishlist grid grid-cols-2 gap-x-5 gap-y-12 sm:grid-cols-3 sm:gap-x-7 sm:gap-y-14" aria-busy="true" aria-label={c.loadingWishlist}>
                {[...Array(3)].map((_, i) => <Skeleton key={i} className="annexes-skeleton rounded-[1.15rem] aspect-[4/5] bg-secondary" />)}
              </div>
            ) : wishlistError ? (
              <div role="alert" className="annexes-empty space-y-4 py-8 text-center"><p>{c.wishlistError}</p><Button className="annexes-action" onClick={() => setRetry(value => value + 1)}>{c.retry}</Button></div>
            ) : wishlist.length === 0 ? (
              <div className="annexes-empty text-center py-16">
                <Heart className="h-12 w-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))]" />
                <p className="font-serif text-xl text-foreground mb-2">{c.emptyWishlist}</p>
                <Button asChild className="annexes-action rounded-xl font-sans mt-4 bg-primary text-primary-foreground">
<Link to={lp('/boutique')}>{c.seeShop}</Link>
</Button>
              </div>
            ) : (
              <div className="annexes-wishlist grid grid-cols-2 gap-x-5 gap-y-12 sm:grid-cols-3 sm:gap-x-7 sm:gap-y-14">
                {wishlist.map(p => <ProductCardV2 key={p.id} product={p} showWishlistBtn />)}
              </div>
            )}
          </TabsContent>

          {/* Addresses */}
          <TabsContent value="addresses">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Existing */}
              <div className="space-y-4">
                <h2 className="font-serif text-lg text-foreground">{c.myAddresses}</h2>
                {(user.addresses || []).length === 0 ? (
                  <p className="font-sans text-sm text-[hsl(var(--muted-foreground))]">{c.noAddress}</p>
                ) : (
                  user.addresses.map(addr => (
                    <div key={addr.id} className="annexes-card annexes-address rounded-2xl bg-card shadow-soft p-4 flex justify-between items-start">
                      <div className="min-w-0 break-words">
                        <p className="font-sans text-xs font-semibold text-[hsl(var(--brand-fuchsia-ink))] mb-1">{addr.label}</p>
                        <p className="font-sans text-sm text-[hsl(var(--foreground))]">{addr.first_name} {addr.last_name}</p>
                        <p className="font-sans text-xs text-[hsl(var(--muted-foreground))]">{addr.address1}</p>
                        <p className="font-sans text-xs text-[hsl(var(--muted-foreground))]">{addr.postal_code} {addr.city}, {addr.country}</p>
                      </div>
                      <button type="button" aria-label={c.deleteAddress(addr.label)} onClick={() => handleDeleteAddress(addr.id)} className="annexes-icon-button text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] transition-colors duration-200">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
              {/* Add */}
              <div>
                <h2 className="font-serif text-lg text-foreground mb-4">{c.addAddress}</h2>
                <form onSubmit={handleAddAddress} aria-busy={addrLoading} className="annexes-card rounded-2xl bg-card shadow-soft p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label htmlFor="address-first-name" className="font-sans text-xs mb-1 block">{c.firstName}</Label><Input id="address-first-name" autoComplete="given-name" value={addrForm.first_name} onChange={e => setAddrForm(p => ({ ...p, first_name: e.target.value }))} required className="rounded-xl font-sans text-sm" /></div>
                    <div><Label htmlFor="address-last-name" className="font-sans text-xs mb-1 block">{c.lastName}</Label><Input id="address-last-name" autoComplete="family-name" value={addrForm.last_name} onChange={e => setAddrForm(p => ({ ...p, last_name: e.target.value }))} required className="rounded-xl font-sans text-sm" /></div>
                  </div>
                  <div><Label htmlFor="address-street" className="font-sans text-xs mb-1 block">{c.address}</Label><Input id="address-street" autoComplete="address-line1" value={addrForm.address1} onChange={e => setAddrForm(p => ({ ...p, address1: e.target.value }))} required className="rounded-xl font-sans text-sm" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label htmlFor="address-postal" className="font-sans text-xs mb-1 block">{c.postalCode}</Label><Input id="address-postal" autoComplete="postal-code" inputMode="numeric" pattern="[0-9]{5}" value={addrForm.postal_code} onChange={e => setAddrForm(p => ({ ...p, postal_code: e.target.value }))} required className="rounded-xl font-sans text-sm" /></div>
                    <div><Label htmlFor="address-city" className="font-sans text-xs mb-1 block">{c.city}</Label><Input id="address-city" autoComplete="address-level2" value={addrForm.city} onChange={e => setAddrForm(p => ({ ...p, city: e.target.value }))} required className="rounded-xl font-sans text-sm" /></div>
                  </div>
                  <Button type="submit" disabled={addrLoading} className="annexes-action w-full rounded-xl font-sans text-sm bg-primary text-primary-foreground">
                    <Plus className="mr-2 h-4 w-4" /> {addrLoading ? c.adding : c.add}
                  </Button>
                </form>
              </div>
            </div>
          </TabsContent>

          {/* Profile */}
          <TabsContent value="profile">
            <div className="max-w-md">
              <form onSubmit={handleUpdateProfile} aria-busy={profileLoading} className="annexes-card rounded-2xl bg-card shadow-soft p-6 space-y-4">
                <div className="flex items-center gap-4 mb-6">
                  <div className="annexes-avatar w-16 h-16 shrink-0 rounded-full bg-[hsl(var(--brand-rose))]/40 flex items-center justify-center">
                    <span className="font-serif text-2xl text-[hsl(var(--primary))]">{user.name?.[0]?.toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-serif text-lg text-foreground">{c.myInfo}</h2>
                    <p className="break-all font-sans text-xs text-[hsl(var(--muted-foreground))]">{user.email}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="base_name" className="font-sans text-sm">{c.fullName}</Label>
                  <Input id="base_name" autoComplete="name" value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} required className="rounded-xl font-sans" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_password" className="font-sans text-sm">{c.newPassword}</Label>
                  <Input id="new_password" type="password" autoComplete="new-password" minLength={8} value={profileForm.password} onChange={e => setProfileForm(p => ({ ...p, password: e.target.value }))} placeholder={c.keepPassword} className="rounded-xl font-sans" />
                </div>

                <Button type="submit" disabled={profileLoading} className="annexes-action w-full rounded-full bg-primary text-primary-foreground font-sans mt-4">
                  {profileLoading ? c.updating : c.updateProfile}
                </Button>

                {user.is_admin && (
                  <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] text-center">
                    <Badge className="rounded-full font-sans text-xs bg-[hsl(var(--brand-gold))]/30 text-[hsl(var(--primary))] border-[hsl(var(--brand-gold))]/50">{c.admin}</Badge>
                  </div>
                )}
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
